import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import { invalidRecipientEmails } from "@productlane/lib/src/email-domain"
import {
	teammateSenderName,
	threadSenderAddress,
} from "@productlane/lib/src/email-settings"
import { forwardCommentHtml } from "@productlane/lib/src/forward-thread"

import type { MutatorTx, Schema } from "../schema"
import type { CreateEmailArgs, ForwardThreadArgs } from "../types"
import type { DeleteID, JSONValue } from "@rocicorp/zero"

export const emailMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateEmailArgs
			ctx: ZeroJwtPayload
		}) => {
			const t0 = performance.now()

			const invalidRecipients = invalidRecipientEmails([
				...args.to,
				...args.cc,
				...args.bcc,
			])
			if (invalidRecipients.length > 0) {
				throw new Error(
					`Not a valid email address: ${invalidRecipients.join(", ")}`,
				)
			}

			// Run independent lookups in parallel to reduce wall-clock time
			// inside the interactive transaction
			const [
				existingEmailResult,
				addresses,
				contact,
				user,
				workspace,
				thread,
				portals,
			] = await Promise.all([
				tx.query.emails.where("id", "=", args.id).run(),
				tx.query.emailSettings.where("workspaceId", authData.workspaceId).run(),
				tx.query.contacts
					.where("workspaceId", authData.workspaceId)
					.where("id", args.contactId)
					.where("isDeleted", false)
					.one()
					.run(),
				tx.query.users.where("id", authData.userId).one().run(),
				tx.query.workspaces.where("id", authData.workspaceId).one().run(),
				tx.query.feedbacks.where("id", args.threadId).one().run(),
				tx.query.portalInstances
					.where("workspaceId", authData.workspaceId)
					.run(),
			])

			const t1 = performance.now()
			console.log(
				`[emails.create shared] parallel lookups: ${(t1 - t0).toFixed(0)}ms`,
			)

			if (existingEmailResult[0]) {
				throw new Error("Email already exists")
			}
			if (addresses.length === 0) {
				throw new Error("Email settings not found")
			}
			if (!contact) {
				throw new Error("Contact not found")
			}
			if (!user || !workspace) {
				throw new Error("User or workspace not found")
			}

			const portal = thread?.portalInstanceId
				? portals.find((p) => p.id === thread.portalInstanceId)
				: undefined
			const sendAddress = threadSenderAddress(addresses, {
				chosenId: args.emailAddressId,
				threadAddressId: thread?.emailAddressId,
				portalAddressId: portal?.defaultEmailAddressId,
			})

			if (!sendAddress) {
				throw new Error("No sending address configured")
			}

			const now = Date.now()

			await tx.mutate.emails.insert({
				id: args.id,
				createdAt: now,
				updatedAt: now,
				date: now,
				version: 1,
				isDeleted: false,
				type: "OUTBOUND",
				subject: args.subject,
				originalRecipient: contact.email,
				preview: "", // The server writes the plain-text preview.
				threadId: args.threadId,
				workspaceId: authData.workspaceId,
				userId: args.userId,
				from: {
					email: sendAddress.primaryEmail,
					name: teammateSenderName({
						senderName: sendAddress.senderName,
						userName: user.name,
						workspaceName: workspace.name,
					}),
					mailboxHash: "",
				} as unknown as JSONValue,
				to: args.to as unknown as JSONValue,
				cc: args.cc as unknown as JSONValue,
				bcc: args.bcc as unknown as JSONValue,
				attachments: args.attachments as unknown as JSONValue,
			})
			// Shares the email's id so the server fills in this same row.
			await tx.mutate.emailContents.insert({
				id: args.id,
				createdAt: now,
				updatedAt: now,
				version: 1,
				isDeleted: false,
				htmlContent: args.content,
				textContent: "",
				attachments: args.attachments as unknown as JSONValue,
				emailId: args.id,
				workspaceId: authData.workspaceId,
			})

			const t2 = performance.now()
			console.log(
				`[emails.create shared] insert email: ${(t2 - t1).toFixed(0)}ms`,
			)

			// The thread follows the address it was last sent from, so replying
			// from another inbox moves it there.
			await tx.mutate.feedbacks.update({
				id: args.threadId,
				lastOutboundMessageAt: now,
				replied: true,
				...(thread && thread.emailAddressId !== sendAddress.id
					? { emailAddressId: sendAddress.id }
					: {}),
			})

			console.log(
				`[emails.create shared] update feedback: ${(performance.now() - t2).toFixed(0)}ms, TOTAL: ${(performance.now() - t0).toFixed(0)}ms`,
			)
		},
	),
	// Sends the conversation to new recipients outside the thread. Only an
	// internal comment lands in the thread; the server job sends the email.
	forward: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: ForwardThreadArgs
			ctx: ZeroJwtPayload
		}) => {
			if (authData.role === "VIEWER") {
				throw new Error("Viewers cannot forward conversations")
			}
			if (args.to.length === 0) {
				throw new Error("Add at least one recipient")
			}
			const invalidRecipients = invalidRecipientEmails([
				...args.to,
				...args.cc,
				...args.bcc,
			])
			if (invalidRecipients.length > 0) {
				throw new Error(
					`Not a valid email address: ${invalidRecipients.join(", ")}`,
				)
			}

			const thread = await tx.query.feedbacks
				.where("workspaceId", authData.workspaceId)
				.where("id", args.threadId)
				.one()
				.run()
			if (!thread) {
				throw new Error("Thread not found")
			}

			const now = Date.now()
			await tx.mutate.comments.insert({
				id: args.id,
				content: forwardCommentHtml(args, args.content),
				attachments: [],
				feedbackId: args.threadId,
				userId: authData.userId,
				createdAt: now,
				updatedAt: now,
				isDeleted: false,
				isResolved: false,
				version: 1,
				workspaceId: authData.workspaceId,
			})
		},
	),
	undo: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: DeleteID<Schema["tables"]["emails"]>
		}) => {
			const email = await tx.query.emails.where("id", "=", args.id).one().run()

			if (!email) {
				throw new Error("Email not found")
			}

			// Hard delete the email
			await tx.mutate.emails.delete({ id: args.id })

			const lastOutboundMessage = await tx.query.emails
				.where("threadId", "=", email.threadId)
				.where("type", "=", "OUTBOUND")
				.orderBy("createdAt", "desc")
				.one()
				.run()

			await tx.mutate.feedbacks.update({
				id: email.threadId,
				lastOutboundMessageAt: lastOutboundMessage?.createdAt ?? null,
			})
		},
	),
}
